/**
 * ThemeToggle — botão de alternar tema (light/dark/system).
 *
 * Comportamento:
 *  - Click simples: alterna entre light e dark (ignora system)
 *  - Long press / dropdown: futuro — escolher system explicitamente
 */

import { Moon, Sun, MonitorCog } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import clsx from "clsx";

import { useTheme, type ThemeMode } from "@/contexts/ThemeContext";

const OPTIONS: { value: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { value: "light",  label: "Claro",    Icon: Sun },
  { value: "dark",   label: "Escuro",   Icon: Moon },
  { value: "system", label: "Sistema",  Icon: MonitorCog },
];

export function ThemeToggle() {
  const { mode, resolved, toggle, setMode } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside fecha o menu
  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    window.addEventListener("mousedown", onClickOutside);
    return () => window.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  const Icon = resolved === "dark" ? Moon : Sun;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={(e) => {
          // Shift+click ou alt+click abre o menu pra escolher 'system'
          if (e.shiftKey || e.altKey) {
            setMenuOpen((v) => !v);
          } else {
            toggle();
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenuOpen((v) => !v);
        }}
        title={`Tema: ${resolved} (alt+click pra opções)`}
        aria-label="Alternar tema"
        className={clsx(
          "inline-flex items-center justify-center h-9 w-9 rounded-lg",
          // Sempre cor clara — vive dentro da TopBar que é sempre dark
          "text-slate-300 hover:text-slate-100 hover:bg-white/5",
          "transition-colors",
        )}
      >
        <Icon className="h-4 w-4 transition-transform duration-200" />
      </button>

      {menuOpen && (
        <div
          role="menu"
          className={clsx(
            "absolute right-0 mt-2 min-w-[140px] rounded-lg border py-1 shadow-card-hover z-50",
            // Menu também vive na topbar dark — usa fundo escuro pra combinar
            "bg-[#0a0f1a] border-white/10 text-slate-200",
          )}
        >
          {OPTIONS.map((opt) => {
            const active = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitem"
                onClick={() => {
                  setMode(opt.value);
                  setMenuOpen(false);
                }}
                className={clsx(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm",
                  "hover:bg-white/5",
                  active && "text-brand-300 font-medium",
                )}
              >
                <opt.Icon className="h-4 w-4 flex-none" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
